import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaSpinner, FaBoxes, FaExclamationTriangle, FaTags, FaBarcode, FaTimes, FaLock } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import ConfirmModal from '../../common/ConfirmModal';
import PartFormDrawer from './PartFormDrawer';
import PartsList from './PartsList';
import { selectStyles, labelClass, isLowStock } from './partsUi';

const QUICK_FILTERS = [
    { key: '', label: 'All parts', Icon: FaBoxes, tone: 'text-[#2a2e30]', test: () => true },
    { key: 'low', label: 'Low stock', Icon: FaExclamationTriangle, tone: 'text-amber-600', test: isLowStock },
    { key: 'stale', label: 'Stale pricing', Icon: FaTags, tone: 'text-red-600', test: (p) => p.is_stale_pricing },
    { key: 'serial', label: 'Serial tracked', Icon: FaBarcode, tone: 'text-[#FF5733]', test: (p) => p.has_serial },
    { key: 'serials_missing', label: 'Serials missing', Icon: FaLock, tone: 'text-red-600', test: (p) => p.has_serial && p.serials_pending }
];

const asArray = (data) => (Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);

export default function CarPartsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const currentUsername = user?.username || user?.name || user?.email || user?.id || 'system';

    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [notice, setNotice] = useState('');

    const [referenceData, setReferenceData] = useState({
        manufacturers: [], categories: [], statuses: [], vatRates: [], vehicleMakes: []
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [quickFilter, setQuickFilter] = useState('');

    const [drawer, setDrawer] = useState({ open: false, part: null });
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const fetchParts = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const res = await axios.get(`${rootUrl}/car_parts`, { headers: { Authorization: `Bearer ${token}` } });
            setParts(asArray(res.data));
        } catch (err) {
            // The API answers 404 when there are no parts yet
            if (err?.response?.status === 404) {
                setParts([]);
            } else {
                console.error(err);
                setLoadError('Could not load parts. Check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchParts();
        const fetchReferenceData = async () => {
            const token = localStorage.getItem('authToken') || 'default-token';
            const headers = { Authorization: `Bearer ${token}` };
            const get = (path) => axios.get(`${rootUrl}${path}`, { headers }).then((r) => asArray(r.data)).catch((err) => {
                console.error(`Failed to load ${path}:`, err);
                return [];
            });
            const [manufacturers, categories, statuses, vatRates, vehicleMakes] = await Promise.all([
                get('/parts/manufacturers'),
                get('/parts/categories'),
                get('/parts/statuses'),
                get('/system/vat-rates'),
                get('/vehicles/makes')
            ]);
            setReferenceData({ manufacturers, categories, statuses, vatRates, vehicleMakes });
        };
        fetchReferenceData();
    }, [fetchParts]);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = setTimeout(() => setNotice(''), 4000);
        return () => clearTimeout(timer);
    }, [notice]);

    const sortedParts = useMemo(
        () => [...parts].sort((a, b) => (a.part_name || '').localeCompare(b.part_name || '')),
        [parts]
    );

    const quickCounts = useMemo(
        () => Object.fromEntries(QUICK_FILTERS.map((f) => [f.key, sortedParts.filter(f.test).length])),
        [sortedParts]
    );

    const filteredParts = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const quick = QUICK_FILTERS.find((f) => f.key === quickFilter);
        return sortedParts.filter((p) => {
            if (quick && !quick.test(p)) return false;
            if (categoryFilter && (p.category || '') !== categoryFilter) return false;
            if (statusFilter && (p.status || '').toUpperCase() !== statusFilter.toUpperCase()) return false;
            if (!term) return true;
            return [p.part_name, p.part_number, p.manufacturer, p.part_id, p.category]
                .some((v) => (v ?? '').toString().toLowerCase().includes(term));
        });
    }, [sortedParts, searchTerm, categoryFilter, statusFilter, quickFilter]);

    const hasFilters = Boolean(searchTerm || categoryFilter || statusFilter || quickFilter);
    const clearFilters = () => {
        setSearchTerm('');
        setCategoryFilter('');
        setStatusFilter('');
        setQuickFilter('');
    };

    const closeDrawer = useCallback(() => setDrawer({ open: false, part: null }), []);

    const handleSaved = (message) => {
        closeDrawer();
        setNotice(message);
        fetchParts();
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        setDeleteError('');
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            await axios.delete(`${rootUrl}/car_parts/${pendingDelete.part_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotice(`${pendingDelete.part_name} deleted`);
            setPendingDelete(null);
            fetchParts();
        } catch (err) {
            console.error(err);
            // The API hides the database reason; linked history (prices, stock, job cards) is the usual cause
            setDeleteError('Could not delete this part. Parts with price changes, stock transactions or job card history can’t be deleted, so set its status to inactive instead.');
        } finally {
            setDeleting(false);
        }
    };

    const categoryOptions = [{ value: '', label: 'All categories' }, ...referenceData.categories.map((c) => ({ value: c.name, label: c.name }))];
    const statusOptions = [{ value: '', label: 'All statuses' }, ...referenceData.statuses.map((s) => ({ value: s.name, label: s.name }))];

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-4 sm:p-6">
            <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#2a2e30]">Manage Parts</h1>
                        <p className="text-sm text-gray-600 mt-1">Parts catalogue, pricing and stock levels</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setDrawer({ open: true, part: null })}
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#FF5733] text-white text-sm font-semibold shadow-sm hover:bg-[#ff4520]"
                    >
                        <FaPlus size={12} /> Add Part
                    </button>
                </div>

                {notice && (
                    <div className="flex items-center justify-between gap-3 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                        <span>{notice}</span>
                        <button type="button" onClick={() => setNotice('')} aria-label="Dismiss" className="p-1 text-green-700"><FaTimes size={12} /></button>
                    </div>
                )}

                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {QUICK_FILTERS.map(({ key, label, Icon, tone }) => {
                        const active = quickFilter === key;
                        return (
                            <button
                                key={key || 'all'}
                                type="button"
                                onClick={() => setQuickFilter(active && key ? '' : key)}
                                aria-pressed={active}
                                className={`text-left rounded-xl bg-white p-3 sm:p-4 shadow-sm border-2 transition-colors ${active ? 'border-[#FF5733]' : 'border-transparent hover:border-orange-200'}`}
                            >
                                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                                    <Icon className={tone} /> {label}
                                </div>
                                <div className={`mt-1 text-2xl font-bold ${tone}`}>
                                    {loading ? '–' : quickCounts[key]}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2">
                            <label htmlFor="parts-search" className={labelClass}>Search</label>
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                                <input
                                    id="parts-search"
                                    type="search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Name, number, manufacturer or ID"
                                    className="w-full h-10 border border-gray-300 rounded-lg pl-9 pr-3 text-sm focus:outline-none focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]"
                                />
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Category</label>
                            <Select
                                styles={selectStyles}
                                options={categoryOptions}
                                value={categoryOptions.find((o) => o.value === categoryFilter) || categoryOptions[0]}
                                onChange={(option) => setCategoryFilter(option?.value || '')}
                                isSearchable
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Status</label>
                            <Select
                                styles={selectStyles}
                                options={statusOptions}
                                value={statusOptions.find((o) => o.value === statusFilter) || statusOptions[0]}
                                onChange={(option) => setStatusFilter(option?.value || '')}
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    <div className={`flex items-center justify-between gap-3 text-sm text-gray-600 ${loading ? 'invisible' : ''}`}>
                        <span>
                            Showing <span className="font-semibold text-gray-900">{filteredParts.length}</span> of {parts.length} parts
                        </span>
                        {hasFilters && (
                            <button type="button" onClick={clearFilters} className="text-[#FF5733] font-semibold hover:underline">
                                Clear filters
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-500">
                            <FaSpinner className="animate-spin mr-3" /> Loading parts...
                        </div>
                    ) : loadError ? (
                        <div className="py-12 text-center space-y-3">
                            <p className="text-sm text-red-500">{loadError}</p>
                            <button type="button" onClick={fetchParts} className="h-10 px-5 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50">
                                Try again
                            </button>
                        </div>
                    ) : filteredParts.length === 0 ? (
                        <div className="py-12 text-center space-y-3">
                            <p className="text-gray-600">
                                {parts.length === 0 ? 'No parts yet. Add your first part to start tracking stock.' : 'No parts match these filters.'}
                            </p>
                            {hasFilters && (
                                <button type="button" onClick={clearFilters} className="text-[#FF5733] text-sm font-semibold hover:underline">
                                    Clear filters
                                </button>
                            )}
                        </div>
                    ) : (
                        <PartsList
                            parts={filteredParts}
                            onEdit={(part) => setDrawer({ open: true, part })}
                            onPriceHistory={(part) => navigate(`/car_parts/${part.part_id}/price-history`)}
                            onStockCard={(part) => navigate(`/parts/stock-cards/${part.part_id}`)}
                            onDelete={(part) => {
                                setDeleteError('');
                                setPendingDelete(part);
                            }}
                        />
                    )}
                </div>
            </div>

            <PartFormDrawer
                open={drawer.open}
                part={drawer.part}
                referenceData={referenceData}
                currentUsername={currentUsername}
                onClose={closeDrawer}
                onSaved={handleSaved}
                onSerialsChanged={fetchParts}
            />

            <ConfirmModal
                isOpen={Boolean(pendingDelete)}
                title="Delete part?"
                description={pendingDelete ? `${pendingDelete.part_name} (${pendingDelete.part_number}) will be permanently removed from the catalogue.` : ''}
                onCancel={() => !deleting && setPendingDelete(null)}
                onConfirm={confirmDelete}
                confirmText="Delete"
                loading={deleting}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
}
